import { useGsap } from "@/hooks/useGsap";
import { gsap, EASE, prefersReducedMotion } from "@/lib/motion";

/**
 * Hero visual, layer 1: scattered work becoming one system.
 *
 * Drawn as SVG, not an image and not a 3D scene. Twelve nodes, fourteen lines,
 * four column rules, nothing else. Every node is rendered at its FINAL,
 * structured coordinate, so a visitor with JavaScript off, a failed bundle or
 * prefers-reduced-motion sees the finished diagram rather than a blank box.
 * The animation only adds a starting offset and takes it away again.
 *
 * This layer is never faded out. It is the crisp version of the picture: 1px
 * hairlines and hard-edged dots are what reads as precise on paper. The
 * Canvas layer on top adds traffic along these same lines; it does not
 * replace them. (2026-09-19: the previous build cross-faded this layer to
 * zero and left a 3px fuzzy band of points in its place, which is why the
 * hero read as a pencil scribble.)
 *
 * Reading order, left to right: the scattered inputs a business already has,
 * the system that holds them, the automation that acts on them, the outcome.
 */

/* Final positions. viewBox is 640 x 440. ParticleField holds the same table;
   if one moves, move both. */
export const NODES = [
  { id: "call", x: 74, y: 96, label: "Phone call", col: 0, off: [-34, -28] },
  { id: "wa", x: 74, y: 168, label: "WhatsApp", col: 0, off: [26, -46] },
  { id: "email", x: 74, y: 240, label: "Email", col: 0, off: [-18, 38] },
  { id: "sheet", x: 74, y: 312, label: "Spreadsheet", col: 0, off: [34, 22] },
  { id: "paper", x: 74, y: 384, label: "Paper and PDFs", col: 0, off: [-28, 44], ldy: -21 },

  { id: "sys-a", x: 268, y: 178, label: "", col: 1, off: [52, -60] },
  { id: "sys-b", x: 268, y: 300, label: "", col: 1, off: [-44, 56] },

  { id: "auto-a", x: 432, y: 132, label: "", col: 2, off: [40, 62] },
  { id: "auto-b", x: 432, y: 240, label: "", col: 2, off: [-56, -34] },
  { id: "auto-c", x: 432, y: 348, label: "", col: 2, off: [30, -58] },

  { id: "out-a", x: 572, y: 204, label: "", col: 3, off: [-48, 50] },
  { id: "out-b", x: 572, y: 276, label: "", col: 3, off: [44, -42] },
];

const byId = (id) => NODES.find((n) => n.id === id);

export const EDGES = [
  ["call", "sys-a"],
  ["wa", "sys-a"],
  ["email", "sys-a"],
  ["sheet", "sys-b"],
  ["paper", "sys-b"],
  ["sys-a", "sys-b"],
  ["sys-a", "auto-a"],
  ["sys-a", "auto-b"],
  ["sys-b", "auto-b"],
  ["sys-b", "auto-c"],
  ["auto-a", "out-a"],
  ["auto-b", "out-a"],
  ["auto-b", "out-b"],
  ["auto-c", "out-b"],
];

/* Column captions. These and the five input labels are the only words in the
   diagram, so the picture stays a picture. */
const COLUMNS = [
  { x: 74, label: "The mess", key: false },
  { x: 268, label: "One system", key: true },
  { x: 432, label: "Automation", key: true },
  { x: 572, label: "Outcome", key: true },
];

/** Radius per role, so the hub reads as the hub and not as one more dot. */
const R = [4, 7, 5, 5.5];

/* A gentle curve reads as a connection; a straight line reads as a table.
   Exported because the Canvas layer samples the identical curve: the points
   must sit ON the hairline, not near it. */
export const edgeGeometry = (a, b) => {
  /* Leave a 9px gap at each end so a line meets a dot rather than covering it. */
  const vertical = Math.abs(a.x - b.x) < 2;
  if (vertical) {
    const gap = 9 + R[a.col] - 4;
    return { vertical: true, x1: a.x, y1: a.y + gap, x2: b.x, y2: b.y - gap };
  }
  const x1 = a.x + R[a.col] + 5;
  const x2 = b.x - R[b.col] - 5;
  return { vertical: false, x1, y1: a.y, x2, y2: b.y, mx: (x1 + x2) / 2 };
};

const toPath = (g) =>
  g.vertical
    ? `M${g.x1} ${g.y1} L${g.x2} ${g.y2}`
    : `M${g.x1} ${g.y1} C${g.mx} ${g.y1} ${g.mx} ${g.y2} ${g.x2} ${g.y2}`;

export default function SystemMap({ className = "" }) {
  const scope = useGsap((ctx, el) => {
    if (prefersReducedMotion()) return;

    const pick = (sel) => el.querySelectorAll(sel);
    const rules = pick("[data-rule]");
    const captions = pick("[data-caption]");
    const inputs = pick('[data-node][data-col="0"]');
    const hubs = pick('[data-node][data-col="1"]');
    const autos = pick('[data-node][data-col="2"]');
    const outs = pick('[data-node][data-col="3"]');
    const edgesAt = (d) => pick(`[data-edge][data-depth="${d}"]`);

    /* Lines are hidden with dashoffset rather than opacity so the stroke
       appears to be drawn rather than faded in. Length is read per path. */
    pick("[data-edge]").forEach((p) => {
      const len = p.getTotalLength ? p.getTotalLength() : 400;
      gsap.set(p, { strokeDasharray: len, strokeDashoffset: len });
    });

    const inputOffsets = NODES.filter((n) => n.col === 0);
    const tl = gsap.timeline({ defaults: { ease: EASE } });

    /* The grid comes first and stays. It is the surface everything lands on. */
    tl.fromTo(rules, { opacity: 0 }, { opacity: 1, duration: 0.7 }, 0);
    tl.fromTo(captions, { opacity: 0, y: 5 }, { opacity: 1, y: 0, duration: 0.45, stagger: 0.06 }, 0.05);

    /* 1 and 2. The mess arrives scattered, then settles. Label travels with
       its dot: they are one group, so the words never detach from the mark. */
    tl.fromTo(
      inputs,
      {
        opacity: 0,
        x: (i) => (inputOffsets[i] ? inputOffsets[i].off[0] : 0),
        y: (i) => (inputOffsets[i] ? inputOffsets[i].off[1] : 0),
      },
      { opacity: 1, x: 0, y: 0, duration: 1.05, stagger: 0.07 },
      0.2
    );

    /* 3. The wires draw in column order, so the eye is pulled left to right
       rather than everything appearing at once. */
    tl.to(edgesAt(0), { strokeDashoffset: 0, duration: 0.7, stagger: 0.06 }, 1.0);
    tl.fromTo(hubs, { opacity: 0, scale: 0.35 }, { opacity: 1, scale: 1, duration: 0.55, stagger: 0.09 }, 1.35);
    tl.to(edgesAt(1), { strokeDashoffset: 0, duration: 0.65, stagger: 0.07 }, 1.6);
    tl.fromTo(autos, { opacity: 0, scale: 0.35 }, { opacity: 1, scale: 1, duration: 0.5, stagger: 0.08 }, 1.9);
    tl.to(edgesAt(2), { strokeDashoffset: 0, duration: 0.6, stagger: 0.07 }, 2.1);
    tl.fromTo(outs, { opacity: 0, scale: 0.35 }, { opacity: 1, scale: 1, duration: 0.5, stagger: 0.1 }, 2.35);
  }, []);

  return (
    <div ref={scope} className={className}>
      <svg
        className="sysmap"
        viewBox="0 0 640 440"
        role="img"
        aria-label="Diagram: phone calls, WhatsApp, email, spreadsheets and paper feeding into one system, which drives automation and produces an outcome."
        focusable="false"
      >
        {/* Column rules. Nearly invisible, but they are what makes the four
            stages line up instead of merely being near each other. */}
        {COLUMNS.map((c) => (
          <line key={`r-${c.label}`} data-rule className="sysmap2__rule" x1={c.x} y1={62} x2={c.x} y2={412} />
        ))}

        {COLUMNS.map((c) => (
          <text
            key={c.label}
            data-caption
            className={`sysmap2__caption ${c.key ? "sysmap2__caption--key" : ""}`}
            x={c.x > 500 ? 622 : c.x}
            y={44}
            textAnchor={c.x > 500 ? "end" : "start"}
          >
            {c.label}
          </text>
        ))}

        {/* Edges. data-depth is the column the line leaves from, which is the
            order they are drawn in. */}
        <g>
          {EDGES.map(([a, b]) => {
            const from = byId(a);
            const to = byId(b);
            return (
              <path
                key={`${a}-${b}`}
                data-edge
                data-depth={from.col}
                className={`sysmap2__edge ${from.col >= 1 ? "sysmap2__edge--core" : ""}`}
                d={toPath(edgeGeometry(from, to))}
              />
            );
          })}
        </g>

        {/* Nodes. The input label lives inside its node group so the word and
            the mark move as one thing. Set above the dot, never beside it:
            every edge leaves a node horizontally and would be drawn straight
            through the type. */}
        <g>
          {NODES.map((n) => (
            <g key={n.id} data-node data-col={n.col}>
              {(n.col === 1 || n.col === 3) && (
                <circle className="sysmap2__ring" cx={n.x} cy={n.y} r={n.col === 1 ? 13 : 11} />
              )}
              <circle
                className={`sysmap2__dot ${n.col >= 1 ? "sysmap2__dot--core" : ""}`}
                cx={n.x}
                cy={n.y}
                r={R[n.col]}
              />
              {n.label ? (
                <text className="sysmap2__tag" x={n.x - 4} y={n.y + (n.ldy || -13)}>
                  {n.label}
                </text>
              ) : null}
            </g>
          ))}
        </g>

        {/* The frame. A single hairline, so the diagram sits on the page as an
            object rather than floating. */}
        <rect x="0.5" y="0.5" width="639" height="439" fill="none" stroke="rgba(22,19,15,0.14)" />
      </svg>
    </div>
  );
}
